import { useMutation } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { Controller, useForm } from 'react-hook-form'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import type { WellPage_WellCulture } from '~/api/backend/pages/wellPageAPI'
import { queryClient } from '~/api/query-client'
import { QUERY_KEYS } from '~/api/query-client-keys'
import Button from '~/components/buttons/Button'
import Input from '~/components/Input'
import LinearScaleIcon from '~/components/icons/LinearScaleIcon'
import Link from '~/components/Link'
import MetadataSidebar, { MetadataItem } from '~/components/MetadataSidebar'
import metadataSidebarStyles from '~/components/metadata_sidebar.scss'
import cs from './well_metadata_sidebar.scss'

type FormData = {
  cellLine: string
  cellLineLot: string
  passageNumber: string
}

export default function WellMetadataSidebar({
  culture,
  routePrefix,
  inTabs,
  cultureLineageEnabled,
  onViewLineageClick,
}: {
  culture: WellPage_WellCulture
  routePrefix?: string
  inTabs?: boolean
  cultureLineageEnabled?: boolean
  onViewLineageClick?: () => void
}) {
  const parent = culture.parentCulture
  const client = useBackendClient()

  const createdAt = dayjs(culture.createdAt)
  const createdThisYear = createdAt.year() === dayjs().year()

  // Check if at least one field is filled
  const isAlreadyFilled =
    culture.cellLine != null ||
    culture.cellLineLot != null ||
    culture.passageNumber != null

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      cellLine: culture.cellLine || '',
      cellLineLot: culture.cellLineLot || '',
      passageNumber: culture.passageNumber?.toString() || '',
    },
  })

  // Mutation
  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      return await client.wellPage.updateData(culture.culturePlate.id, culture.well, {
        cell_line: data.cellLine || null,
        cell_line_lot: data.cellLineLot || null,
        passage_number: data.passageNumber ? Number(data.passageNumber) : null,
      })
    },
    onSuccess: () => {
      // Invalidate the well page query
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.WELL_PAGE, culture.culturePlate.id, culture.well],
      })
    },
  })

  const onSubmit = (data: FormData) => {
    mutation.mutate(data)
  }

  return (
    <MetadataSidebar
      className='w-full'
      childClassName={inTabs ? metadataSidebarStyles['no-border-top'] : ''}
    >
      <MetadataItem label={parent == null ? 'First Checked In' : 'Seeded'}>
        {createdAt.format(createdThisYear ? 'MMM D' : 'MMM D, YYYY')}&ensp;
        {createdAt.format('h:mm A')}&ensp;
        <span className={cs.secondary}>({createdAt.fromNow()})</span>
      </MetadataItem>

      <MetadataItem label='Parent Culture'>
        {parent ? (
          <Link
            to={`${routePrefix ?? ''}/plate/${parent.culturePlate.id}/well/${parent.well}`}
            variant='primary'
          >
            {parent.culturePlate.barcode} • {parent.well}
          </Link>
        ) : (
          <span className={cs.secondary}>None</span>
        )}
      </MetadataItem>

      {cultureLineageEnabled && onViewLineageClick && (
        <MetadataItem label=''>
          <button
            type='button'
            className='font-subtext text-accent hover:text-accent-light flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0 transition-colors'
            onClick={onViewLineageClick}
          >
            <LinearScaleIcon className='w-5 h-5 fill-current' />
            View Lineage
          </button>
        </MetadataItem>
      )}

      {isAlreadyFilled ? (
        <>
          <MetadataItem label='Cell Line'>
            {culture.cellLine || <span className={cs.secondary}>None</span>}
          </MetadataItem>

          <MetadataItem label='Cell Line Lot'>
            {culture.cellLineLot || <span className={cs.secondary}>None</span>}
          </MetadataItem>

          <MetadataItem label='Passage Number'>
            {culture.passageNumber ?? <span className={cs.secondary}>None</span>}
          </MetadataItem>
        </>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className='!flex flex-col gap-4 mt-4'>
          <Controller
            name='cellLine'
            control={control}
            rules={{
              validate: (value, formValues) => {
                // At least one field must be filled
                if (!value && !formValues.cellLineLot && !formValues.passageNumber) {
                  return 'At least one field is required'
                }
                return true
              },
            }}
            render={({ field }) => (
              <Input
                label='Cell Line'
                inputClassName='w-full max-w-lg'
                errorMessage={errors.cellLine?.message}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <Controller
            name='cellLineLot'
            control={control}
            rules={{
              validate: (value, formValues) => {
                // At least one field must be filled
                if (!value && !formValues.cellLine && !formValues.passageNumber) {
                  return 'At least one field is required'
                }
                return true
              },
            }}
            render={({ field }) => (
              <Input
                label='Cell Line Lot'
                inputClassName='w-full max-w-lg'
                errorMessage={errors.cellLineLot?.message}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <Controller
            name='passageNumber'
            control={control}
            rules={{
              validate: (value, formValues) => {
                // At least one field must be filled
                if (!value && !formValues.cellLine && !formValues.cellLineLot) {
                  return 'At least one field is required'
                }
                // Must be a positive integer if provided
                if (value) {
                  const num = Number(value)
                  if (Number.isNaN(num)) {
                    return 'Must be a valid number'
                  }
                  if (!Number.isInteger(num)) {
                    return 'Must be a whole number'
                  }
                  if (num <= 0) {
                    return 'Must be a positive number'
                  }
                }
                return true
              },
            }}
            render={({ field }) => (
              <Input
                label='Passage Number'
                inputClassName='w-full max-w-16'
                errorMessage={errors.passageNumber?.message}
                value={field.value}
                onChange={value => {
                  // Only allow positive integers during input
                  // Allow empty string or digits only, and filter out 0 at the start
                  if (value === '') {
                    field.onChange(value)
                  } else if (/^\d+$/.test(value)) {
                    // Remove leading zeros and ensure it's a positive integer > 0
                    const numValue = parseInt(value, 10)
                    if (numValue > 0) {
                      field.onChange(numValue.toString())
                    }
                  }
                }}
              />
            )}
          />

          <Button
            type='primary'
            label={mutation.isPending ? 'Saving...' : 'Save'}
            onClick={handleSubmit(onSubmit)}
            disabled={mutation.isPending}
            className='w-fit'
          />

          {mutation.isError && (
            <div className='text-red-500 text-sm'>
              Failed to update metadata. Please try again.
            </div>
          )}
        </form>
      )}
    </MetadataSidebar>
  )
}
